import { api } from './api'

/** Published lessons, optionally filtered by level. No pagination — the API returns the full list. */
export async function listLessons({ level } = {}) {
  const { data } = await api.get('/lessons/list', { params: { level } })
  return data
}

export async function getLesson(lessonId) {
  const { data } = await api.get(`/lessons/detail/${lessonId}`)
  return data
}

/**
 * Upload one sentence take and wait for its pronunciation assessment.
 *
 * Synchronous by design (proposal §5.4): the request stays open until Azure
 * has scored the recording, which is why callers should show a determinate
 * "analysing" state rather than a spinner that looks stuck. Only WAV
 * (PCM 16kHz mono) or OGG/Opus is accepted — see hooks/useAudio.js.
 *
 * Plan B (V9-b) is opted into on every upload: with `acceptQueued`, a take
 * Azure cannot score right now — it timed out, it was unreachable, or the
 * admin has `planB.enabled` on — comes back as a 202 `AttemptQueuedDto`
 * (`{ attemptId, status: 'queued', … }`) instead of a 504/502, and is
 * scored later by the requeue job or by an admin. So this answers either
 * shape, and `isQueuedAnswer` (lib/practiceSession) is how callers tell
 * them apart; the immediate refusals — `audio_rejected`,
 * `speech_not_recognised` — still throw exactly as before.
 *
 * `clientAttemptId` is the recording's own id, stable across every upload
 * of it (lib/outbox.js): a second upload under the same id answers the row
 * the first one made instead of scoring the same audio twice.
 */
export async function submitAttempt(
  lessonId,
  sentenceIndex,
  blob,
  filename = 'recording.wav',
  { onUploadProgress, signal, clientAttemptId, acceptQueued = true, localReps, localPracticeSeconds, transcriptOpenedLocally } = {},
) {
  const form = new FormData()
  form.append('lessonId', lessonId)
  form.append('sentenceIndex', String(sentenceIndex))
  form.append('file', blob, filename)
  // Multipart carries text, not JSON — the backend reads the string 'true'.
  if (acceptQueued) form.append('acceptQueued', 'true')
  if (clientAttemptId) form.append('clientAttemptId', clientAttemptId)
  // R13: the repetitions said on the device before this take — counts only, never their audio.
  if (Number.isInteger(localReps)) form.append('localReps', String(localReps))
  if (Number.isInteger(localPracticeSeconds)) form.append('localPracticeSeconds', String(localPracticeSeconds))
  // R1 (owner, 2026-10-14): the text was opened here by three wrong repetitions — the server cuts the XP as after its own lock.
  if (transcriptOpenedLocally === true) form.append('transcriptOpenedLocally', 'true')

  // `onUploadProgress` lets the review tick its upload step on the real last
  // byte; `signal` lets «لغو» abort the wait (the take itself stays).
  const { data } = await api.post('/attempts/submit', form, { onUploadProgress, signal })
  return data
}

export async function listHistory({ lessonId } = {}) {
  const { data } = await api.get('/attempts/list', { params: { lessonId } })
  return data
}

/** One attempt with its stored assessment — `attempts_detail` (B2), to reopen a result. */
export async function getAttempt(attemptId) {
  const { data } = await api.get(`/attempts/detail/${attemptId}`)
  return data
}

/**
 * The learner's own recording as a `blob:` URL for an `<audio>` element.
 *
 * `userAudioUrl` is relative to the API base (`attempts/audio/{id}`) and
 * bearer-gated (B1) — an `<audio src>` cannot carry the token, so the bytes
 * come through the authenticated client and are handed to the element as an
 * object URL. The caller owns that URL and revokes it when done.
 */
export async function fetchAttemptAudio(userAudioUrl) {
  const { data } = await api.get(userAudioUrl, { responseType: 'blob' })
  return URL.createObjectURL(data)
}

export async function myStats() {
  const { data } = await api.get('/me/stats')
  return data
}

